import { z } from 'zod'

import {
  creativeSchema,
  targetingEntrySchema,
  campaignTargetingSchema,
  integrationDetailSchema,
  platformOverridesSchema,
} from '../campaignSetup/schemas'
import { integrationDetailSchema as enrichedIntegrationDetailSchema } from '../integrationAssets/schemas'

// Grounded against fd-client-api-google-ads source (not just the FE migration-guide
// PDF, which this epic has repeatedly found stale in places):
//   app/api/v4/events/events.py       — event_campaigns_response_model (~line 511),
//                                        campaigns_parameters_serializer (~line 137)
//   app/api/v4/campaigns_setup.py     — campaign_setup_serializer (~line 137)
//   app/core/event_campaign_service.py, app/model/event_campaign.py — how these
//                                        fields actually get written, not just read

export const campaignSetupStatusSchema = z
  .object({
    channel: z.string(),
    campaign_status: z.string().nullable().optional(),
    ad_group_status: z.string().nullable().optional(),
    budget_average: z.number().nullable().optional(),
  })
  .passthrough()

// event_campaign.targeting entries are platform-nested ({tc, tc_run_id, fb,
// google}) for a natively-v4-boosted event, and for any TC that has been
// through this module's editPublishedTcAsync (edit_campaign_setup always
// re-merges the platform-nested unified_targeting request straight back onto
// event_campaign.targeting, see event_campaign_service.py). But for a legacy
// (v3-boosted) event synthesized on first v4 touch and never edited since,
// event_campaign.targeting is instead a FLAT single-channel snapshot
// (age_range/targeting_mode directly, no fb/google wrapper) copied verbatim
// from whichever CampaignSetup row synthesis picked as canonical — prefers
// "fb", see synthesize_event_campaign_from_setups in event_campaign_service.py.
// The migration guide's "response shape is identical for legacy and native v4
// events" claim does NOT hold for this specific field once you read the model
// code, only for `creative` (always the unified shape either way). Reusing
// targetingEntrySchema here since it tolerates both: fb/google are optional,
// and .passthrough() lets the flat legacy keys through untyped. See
// mappers.js's normalizeTcTargeting for the shape-detection/normalization.
export const tasteClusterTargetingSchema = targetingEntrySchema

export const tasteClusterSchema = z
  .object({
    tc: z.string(),
    tc_run_id: z.number(),
    published: z.boolean(),
    campaign_setups: z.array(campaignSetupStatusSchema),
    creative: creativeSchema.nullable().optional(),
    targeting: tasteClusterTargetingSchema.nullable().optional(),
  })
  .passthrough()

export const eventCampaignHeaderSchema = z
  .object({
    id: z.number(),
    total_budget: z.number().nullable().optional(),
    goal: z.string().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    platforms: z.array(z.string()).optional(),
    status: z.string().optional(),
    integration_details: z.array(integrationDetailSchema).optional(),
    campaign_targeting: campaignTargetingSchema.nullable().optional(),
    platform_overrides: platformOverridesSchema.nullable().optional(),
  })
  .passthrough()

// GET /events/{id}/campaign_setups — explicitly replaces/deprecates the old
// campaigns list's embedded `latest_campaign_setup` field (single-TC only, no
// campaign header, no per-channel status). event_campaign is null when the
// event has no CampaignSetup rows at all yet (no published TCs) — not an
// error case, see EventCampaignSetups.get's `ec_payload = None` branch.
export const campaignSetupsResponseSchema = z
  .object({
    event_campaign: eventCampaignHeaderSchema.nullable().optional(),
    taste_clusters: z.array(tasteClusterSchema),
  })
  .passthrough()

// GET /events/{id}/default_campaigns_parameters — integration_details here
// are the *enriched* (live-validated) shape, unlike the stored ones on
// event_campaign/taste_clusters above, so this reuses
// domain/integrationAssets/schemas.js's integrationDetailSchema (valid/
// is_active/account_status/etc.) rather than campaignSetup's plainer one.
export const defaultCampaignParametersResponseSchema = z
  .object({
    total_budget: z.number().nullable().optional(),
    goal: z.string().nullable().optional(),
    // Confirmed live: get_default_campaign_parameters's return dict never
    // sets a "tcs" key at all (only total_budget/goal/integration_details/
    // meta_access_error) — flask-restx marshals the missing
    // fields.List(fields.String) as a literal JSON null, not an omitted key
    // or []. Needs .nullable(), not just .optional().
    tcs: z.array(z.string()).nullable().optional(),
    integration_details: z.array(enrichedIntegrationDetailSchema).nullable().optional(),
    meta_access_error: z.boolean().optional(),
  })
  .passthrough()
